import { Component, ElementRef, HostListener, Input, OnDestroy, OnInit, Renderer2, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import { ModalHostDirective } from '@shared/modal/directives/modal-host.directive';
import { ModalService } from '@shared/modal/service/modal.service';
import { Modal } from '@shared/modal/models/modal.interface';
import { ModalSize } from '@shared/modal/models/modal.size';

@Component({
    selector: 'pim-modal',
    templateUrl: './modal.component.html',
    styleUrls: ['./modal.component.sass'],
    standalone: false
})
export class ModalComponent implements OnInit, OnDestroy {

	visible: boolean = false;

	closeButtonVisible: boolean = true;
	escapeCloseActive: boolean = true;

	@Input()
	cssClass!: string;

	@Input()
	size: ModalSize = 'big';

	@ViewChild(ModalHostDirective, {static: true})
	modalHost!: ModalHostDirective;

	@ViewChild('modalContainer', {static: false})
	modalContainer!: ElementRef;

	private timeouts: number[] = [];
	private modal!: Modal | undefined;
	private subscription: Subscription = new Subscription();

	private readonly body!: HTMLBodyElement | null;

	constructor(private modalService: ModalService,
				private renderer: Renderer2) {
		this.body = document.querySelector('body');
	}

	ngOnInit(): void {
		this.modalService.setComponent(this);
		this.subscription = this.modalService.subject.subscribe(
			(modal: Modal) => {
				this.modal = modal;
				this.show();
			}
		);
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
		this.timeouts.forEach((timeout: number) => {
			clearTimeout(timeout);
		});
	}

	@HostListener('document:keyup', ['$event'])
	keyup(event: KeyboardEvent): void {
		switch (event.key) {
			case 'Escape': {
				if (this.escapeCloseActive) {
					this.close(true);
				}
				break;
			}
			case 'Enter': {
				this.handleEnter();
				break;
			}
			case 'Tab': {
				this.handleTab(event);
				break;
			}
		}
	}

	// TODO: Material Tab-Trap probieren
	// Confine tab-targets withing the modal, otherwise tab selects background-elements or event browser-elements
	private handleTab(event: KeyboardEvent): void {
		if (!this.modalService.componentLoaded()) {
			return;
		}
		const modal = this.modalContainer.nativeElement;

		if (!this.isTargetInsideModal(event.target)) {
			event.preventDefault();
			this.focusFirstInput(modal);
		}
	}

	private focusFirstInput(modal: any): void {
		const nextInputSelector = `input[tabindex="1"], select[tabindex="1"]`;

		const nextInput = modal.find(nextInputSelector);
		if (nextInput.length === 0) {
			const inputList = modal.find('input, select');
			if (inputList.length > 0) {
				inputList.get(0).focus();
			}
		} else {
			nextInput.focus();
		}
	}

	private isTargetInsideModal(target: EventTarget | null): boolean {
		return this.modalContainer.nativeElement.contains(target);
	}

	@HostListener('window:popstate')
	browserBack(): void {
		this.close(true);
	}

	private handleEnter(): void {
		const focus = document.querySelector(':focus');
		if (!focus) {
			return;
		}
		if (focus.getAttribute('type') === 'submit' || focus.tagName === 'TEXTAREA') {
			return;
		}
	}

	private show(): void {
		this.visible = true;
		this.setBodyClass();
	}

	private setBodyClass(): void {
		this.renderer.addClass(this.body, 'modal-open');
	}

	private removeBodyClass(): void {
		this.renderer.removeClass(this.body, 'modal-open');
	}

	close(userClosedManually: boolean = false, returnData?: any): void {
		if (this.modal === undefined) {
			return;
		}

		this.modal.modalOnClose(userClosedManually, returnData);
		const timeout = window.setTimeout(() => {
			this.visible = false;
			this.modalService.destroyComponent();
			this.modal = undefined;
			this.removeBodyClass();
		}, 0);
		this.timeouts.push(timeout);
		this.closeButtonVisible = true;
		this.escapeCloseActive = true;
	}
}
